import React, { useState, useEffect, useRef } from "react";

const JudgesDisplay = ({ competitionId, routes, onBack, t, isQuadMode }) => {
  const [judgesData, setJudgesData] = useState(null);
  const [loading, setLoading] = useState(true);
  const judgesDisplayRef = useRef(null);
  const scrollIntervalRef = useRef(null);
  const scrollAnimationRef = useRef(null);

  // Конфигурация скорости скролла (пикселей в секунду)
  const SCROLL_SPEED = 50; // пикселей в секунду
  const STOP_AT_BOTTOM = 3000;
  const STOP_AT_TOP = 3000;

  // Функция для равномерного скролла с контролируемой скоростью
  const smoothScrollTo = (targetPosition) => {
    return new Promise((resolve) => {
      if (!judgesDisplayRef.current) {
        resolve();
        return;
      }

      const element = judgesDisplayRef.current;
      const startPosition = element.scrollTop;
      const distance = targetPosition - startPosition;
      
      if (Math.abs(distance) < 1) {
        resolve();
        return;
      }

      const duration = Math.abs(distance) / SCROLL_SPEED * 1000; // миллисекунды
      const startTime = performance.now();

      const animateScroll = (currentTime) => {
        const elapsed = currentTime - startTime;
        const progress = Math.min(elapsed / duration, 1);
        
        // Линейная анимация (равномерная скорость)
        const currentPosition = startPosition + (distance * progress);
        element.scrollTop = currentPosition;

        if (progress < 1) {
          scrollAnimationRef.current = requestAnimationFrame(animateScroll);
        } else {
          scrollAnimationRef.current = null;
          resolve();
        }
      };      scrollAnimationRef.current = requestAnimationFrame(animateScroll);
    });
  };

  // Функция для остановки текущей анимации скролла
  const stopScrollAnimation = () => {
    if (scrollAnimationRef.current) {
      cancelAnimationFrame(scrollAnimationRef.current);
      scrollAnimationRef.current = null;
    }
  };
  const checkOverflow = () => {
    if (!judgesDisplayRef.current) return false;
    
    const element = judgesDisplayRef.current;
    return element.scrollHeight > element.clientHeight;
  };

  // Функция для выполнения одного цикла автоскролла (до конца и обратно)
  const performAutoScroll = async () => {
    if (!judgesDisplayRef.current || !checkOverflow()) return;
    
    const element = judgesDisplayRef.current;
    const maxScroll = element.scrollHeight - element.clientHeight;
    
    // Плавно прокручиваем до конца с контролируемой скоростью
    await smoothScrollTo(maxScroll);
    
    // Небольшая пауза в конце перед прокруткой обратно
    await new Promise(resolve => setTimeout(resolve, STOP_AT_BOTTOM));
    
    // Плавно прокручиваем обратно в начало
    await smoothScrollTo(0);
    
    // Пауза в начале перед следующим циклом
    await new Promise(resolve => setTimeout(resolve, STOP_AT_TOP));
  };

  // Функция для запуска циклического автоскролла
  const startCyclicScroll = () => {
    if (!judgesDisplayRef.current || !checkOverflow()) return;
    
    // Очищаем предыдущий интервал если есть
    if (scrollIntervalRef.current) {
      clearInterval(scrollIntervalRef.current);
    }
    
    // Вычисляем время одного полного цикла
    const element = judgesDisplayRef.current;
    const maxScroll = element.scrollHeight - element.clientHeight;
    const timeToScrollDown = (maxScroll / SCROLL_SPEED) * 1000; // миллисекунды
    const timeToScrollUp = timeToScrollDown; // то же время на обратный путь
    const pauseTime = STOP_AT_BOTTOM + STOP_AT_TOP; // общее время пауз
    const totalCycleTime = timeToScrollDown + timeToScrollUp + pauseTime;
    
    // Запускаем первый цикл сразу
    performAutoScroll();
    
    // Устанавливаем интервал для повторения
    scrollIntervalRef.current = setInterval(() => {
      performAutoScroll();
    }, totalCycleTime);
  };

  // Функция для остановки циклического скролла
  const stopCyclicScroll = () => {
    if (scrollIntervalRef.current) {
      clearInterval(scrollIntervalRef.current);
      scrollIntervalRef.current = null;
    }
    stopScrollAnimation();
  };

  useEffect(() => {
    fetchJudgesData();
  }, []);

  // useEffect для запуска циклического автоскролла после загрузки данных о судьях
  useEffect(() => {
    if (judgesData && judgesData.judges && judgesData.judges.length > 0) {
      // Небольшая задержка для завершения рендеринга
      const timer = setTimeout(() => {
        startCyclicScroll();
      }, 500);

      return () => {
        clearTimeout(timer);
        stopCyclicScroll();
      };
    }
  }, [judgesData]);

  // useEffect для cleanup при размонтировании компонента
  useEffect(() => {
    return () => {
      stopCyclicScroll();
    };
  }, []);

  const fetchJudgesData = async () => {
    try {
      const response = await fetch(routes.judges_data, {
        method: 'GET',
        headers: {
          'X-Requested-With': 'XMLHttpRequest',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').content
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      setJudgesData(data);
    } catch (error) {
      console.error('Error fetching judges data:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="judges-display-loading">
        <div className="text-center">
          <div className="spinner-border" role="status">
            <span className="visually-hidden">{t.loading}</span>
          </div>
        </div>
      </div>
    );
  }

  if (!judgesData || !judgesData.judges.length) {
    return (
      <div className="judges-display-empty">
        <div className="text-center">
          <h3>{t.judges}</h3>
          <p>Нет судей в соревновании</p>
          <button className="btn btn-secondary" onClick={onBack}>
            <i className="fa-solid fa-arrow-left me-2"></i>
            {t.back}
          </button>
        </div>
      </div>
    );
  }

  const judgesPerRow = isQuadMode ? 1 : (judgesData.judges.length <= 3 ? judgesData.judges.length : 3);
  const totalRows = Math.ceil(judgesData.judges.length / judgesPerRow);

  return (
    <div className={`judges-display ${isQuadMode ? 'quad-mode' : 'single-mode'}`} ref={judgesDisplayRef}>
      <div className="judges-grid mb-4">
        {judgesData.judges.map((judge, index) => (
          <div key={judge.id} className="judge-card">
            <div className="judge-info">
              <div className="judge-name">
                {judge.last_name} {judge.first_name}
              </div>
            </div>
            <div className="judge-photo">
              {judge.photo_url ? (
                <img
                  src={judge.photo_url}
                  alt={judge.full_name}
                  className="judge-photo-img"
                />
              ) : (
                <div className="judge-photo-placeholder">
                  <i className="fa-solid fa-user"></i>
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
      <div className="mt-4">
        <br />
        <br />
      </div>
    </div>
  );
};

export default JudgesDisplay;